/* eslint-disable react/no-children-prop */
import React from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import useMealPlanDetails from "./hooks/useMealPlanDetails";
import MealPlanGeneratorDialog from "./MealPlanGeneratorDialog";
import RecipePreviewDialog from "./components/mealplan/RecipePreviewDialog";
import {
  MealPlanDetailsContent,
  MealPlanFloatingActions,
} from "./components/mealplan";

/**
 * MealPlanDetails component displays detailed information about a meal plan
 * including recipes, ingredients list, and nutritional information.
 * It also provides functionality to edit the meal plan.
 *
 * @param {Object} mealPlan - The meal plan object to display
 * @param {Function} getRecipesFromList - Function to fetch recipes from a list
 */
const MealPlanDetails = ({ mealPlan, getRecipesFromList }) => {
  console.log("MealPlanDetails rendered with mealPlan:", mealPlan);

  const [editingMealPlan, setEditingMealPlan] = React.useState(null);
  const [recipePreview, setRecipePreview] = React.useState(null);
  const { orgId } = useParams();

  if (!mealPlan) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        flex={1}
        p={4}
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading meal plan...
        </Typography>
      </Box>
    );
  }

  // Use our custom hook to handle meal plan data processing
  const {
    scheduledRecipes,
    ingredients,
    weeklySchedule,
    dailyNutritionTotals,
    overallAverages,
    isLoading,
  } = useMealPlanDetails(mealPlan, getRecipesFromList);

  // Handle edit meal plan action
  const handleEditMealPlan = () => {
    setEditingMealPlan(mealPlan);
  };

  // Handle recipe click to show preview
  const handleRecipeClick = (recipe) => {
    setRecipePreview(recipe);
  };

  // Handle recipe preview dialog close
  const handleRecipePreviewClose = () => {
    setRecipePreview(null);
  };

  // Handle dialog close and potential save
  const handleDialogClose = async (result) => {
    setEditingMealPlan(null);

    if (result && result.isEditMode) {
      try {
        // Update the meal plan using Firebase directly
        const { consola } = await import("consola");
        const firebase = (await import("firebase/compat/app")).default;

        const { selectedRecipes, mealPlanMetadata } = result;

        // For simplicity, we'll just update the selectedRecipes and recalculate totals
        // In a real app, you might want to extract this to a shared utility
        const mealPlanRef = firebase
          .firestore()
          .collection("organizations")
          .doc(orgId)
          .collection("mealPlans")
          .doc(mealPlanMetadata.id);

        // Simple update - in production you might want to handle new recipes too
        const updateData = {
          ...mealPlanMetadata,
          selectedRecipes: selectedRecipes,
          updatedOn: new Date().toISOString(),
        };

        await mealPlanRef.update(updateData);
        consola.success(
          `Successfully updated meal plan: ${mealPlanMetadata.name}`,
        );

        // Optionally refresh the page or show success message
        window.location.reload(); // Simple refresh for now
      } catch (error) {
        console.error("Error updating meal plan:", error);
        // Optionally show error message to user
      }
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="50vh"
      >
        <CircularProgress size={30} />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading recipes...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      <MealPlanDetailsContent
        mealPlan={mealPlan}
        recipes={scheduledRecipes}
        ingredients={ingredients}
        weeklySchedule={weeklySchedule}
        dailyNutritionTotals={dailyNutritionTotals}
        overallAverages={overallAverages}
        onEditClick={handleEditMealPlan}
        onRecipeClick={handleRecipeClick}
        organizationId={orgId}
        mealPlanId={mealPlan.id}
      />

      <MealPlanFloatingActions
        mealPlan={mealPlan}
        onEditClick={handleEditMealPlan}
        ingredients={ingredients}
        recipes={scheduledRecipes}
        weeklySchedule={weeklySchedule}
      />

      {/* Meal Plan Edit Dialog */}
      {editingMealPlan && (
        <MealPlanGeneratorDialog
          existingMealPlan={editingMealPlan}
          onDialogClosed={handleDialogClose}
        />
      )}

      {/* Recipe Preview Dialog */}
      <RecipePreviewDialog
        open={!!recipePreview}
        onClose={handleRecipePreviewClose}
        recipe={recipePreview}
      />
    </Box>
  );
};

export default MealPlanDetails;

